import React, {useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  ScrollView,
  ViewStyle,
  TextStyle,
  useWindowDimensions,
  Pressable,
  Animated,
  Platform,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {colors} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

const LAST_VIEWED_DISPLAY_KEY = 'oma_last_viewed_display_id';

// Synchronous localStorage access for navigation (web only, native uses different approach)
const getLastViewedDisplayId = (): string | undefined => {
  if (Platform.OS !== 'web') return undefined;
  try {
    return localStorage.getItem(LAST_VIEWED_DISPLAY_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const theme = colors.dark;

// ============================================================================
// Layout Components
// ============================================================================

interface DarkScreenProps {
  children: React.ReactNode;
  backgroundImage?: string;
}

export function DarkScreen({children, backgroundImage}: DarkScreenProps) {
  return (
    <View style={styles.screen}>
      {/* Blurred background */}
      {backgroundImage && (
        <View style={styles.backgroundContainer}>
          <Image
            source={{uri: backgroundImage}}
            style={styles.backgroundImage}
            blurRadius={20}
          />
          <View style={styles.backgroundOverlay} />
        </View>
      )}
      {!backgroundImage && <View style={styles.backgroundSolid} />}
      {children}
    </View>
  );
}

interface GlassPanelProps {
  children: React.ReactNode;
  style?: ViewStyle;
  centered?: boolean;
}

export function GlassPanel({children, style, centered}: GlassPanelProps) {
  return (
    <View style={[
      styles.glassPanel,
      centered && styles.glassPanelCentered,
      style,
    ]}>
      {children}
    </View>
  );
}

// ============================================================================
// Settings Layout Component
// ============================================================================

interface SettingsLayoutProps {
  children: React.ReactNode;
  currentScreen: 'home' | 'gallery' | 'calendar' | 'account' | 'displays' | 'donation';
  onNavigate: (screen: 'Settings' | 'GallerySettings' | 'CalendarSettings' | 'AccountSettings' | 'Displays' | 'Donation' | 'Frame', params?: {displayId?: string}) => void;
  onLogout: () => void;
}

export function SettingsLayout({children, currentScreen, onNavigate, onLogout}: SettingsLayoutProps) {
  const {t} = useTranslation();
  const {width} = useWindowDimensions();
  const isWide = width >= 768;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const insets = useSafeAreaInsets();

  const menuItems = [
    {id: 'home' as const, icon: 'home', label: t('settings.title'), screen: 'Settings' as const},
    {id: 'gallery' as const, icon: 'photo_library', label: t('settings.gallery'), screen: 'GallerySettings' as const},
    {id: 'calendar' as const, icon: 'calendar_month', label: t('settings.calendar'), screen: 'CalendarSettings' as const},
    {id: 'displays' as const, icon: 'tv', label: t('settings.displays'), screen: 'Displays' as const},
    {id: 'account' as const, icon: 'person', label: t('settings.account'), screen: 'AccountSettings' as const},
    // Stripe donations only on web (Google Play requires Play Billing)
    ...(Platform.OS === 'web' ? [{id: 'donation' as const, icon: 'cookie', label: t('settings.donation'), screen: 'Donation' as const}] : []),
  ];

  const handleMenuItemPress = (screen: 'Settings' | 'GallerySettings' | 'CalendarSettings' | 'AccountSettings' | 'Displays' | 'Donation' | 'Frame') => {
    setMobileMenuOpen(false);
    onNavigate(screen);
  };

  const handleBackToPhotos = () => {
    setMobileMenuOpen(false);
    const displayId = getLastViewedDisplayId();
    onNavigate('Frame', {displayId});
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    onLogout();
  };

  const renderSidebarContent = () => (
    <>
      <View style={styles.sidebarContent}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleBackToPhotos}
          activeOpacity={0.7}
          accessibilityLabel={t('frame.backToPhotos')}
          accessibilityRole="button">
          <View style={styles.backButtonIcon}>
            <Text maxFontSizeMultiplier={1} style={styles.backButtonIconText} accessible={false}>arrow_back</Text>
          </View>
          <Text style={styles.backButtonLabel}>{t('frame.backToPhotos')}</Text>
        </TouchableOpacity>

        <Text style={styles.sidebarTitle}>{t('settings.title')}</Text>

        <View style={styles.menuList}>
          {menuItems.map((item) => (
            <TouchableOpacity
              key={item.id}
              style={[
                styles.menuItem,
                currentScreen === item.id && styles.sidebarMenuItemActive,
              ]}
              onPress={() => handleMenuItemPress(item.screen)}
              activeOpacity={0.7}
              accessibilityLabel={item.label}
              accessibilityRole="menuitem">
              <Text maxFontSizeMultiplier={1} style={[
                styles.menuItemIcon,
                styles.materialIcon,
                currentScreen === item.id && styles.sidebarMenuItemIconActive,
              ]} accessible={false}>
                {item.icon}
              </Text>
              <Text style={[
                styles.menuItemLabel,
                currentScreen === item.id && styles.sidebarMenuItemLabelActive,
              ]}>
                {item.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      <TouchableOpacity
        style={[styles.menuItem, styles.menuItemDestructive]}
        onPress={handleLogout}
        activeOpacity={0.7}
        accessibilityLabel={t('common.logout')}
        accessibilityRole="button">
        <Text maxFontSizeMultiplier={1} style={[styles.menuItemIcon, styles.materialIcon, styles.menuItemIconDestructive]} accessible={false}>
          logout
        </Text>
        <Text style={[styles.menuItemLabel, styles.menuItemLabelDestructive]}>
          {t('common.logout')}
        </Text>
      </TouchableOpacity>
    </>
  );

  return (
    <DarkScreen>
      <View style={styles.settingsContainer}>
        {/* Mobile Menu Button */}
        {!isWide && (
          <TouchableOpacity
            style={[styles.mobileMenuButton, {top: insets.top + 16}]}
            onPress={() => setMobileMenuOpen(true)}
            activeOpacity={0.7}
            accessibilityLabel={t('common.openMenu')}
            accessibilityRole="button">
            <Text maxFontSizeMultiplier={1} style={styles.mobileMenuButtonIcon} accessible={false}>menu</Text>
          </TouchableOpacity>
        )}

        {/* Mobile Sidebar Overlay */}
        {!isWide && mobileMenuOpen && (
          <View style={styles.mobileMenuOverlay}>
            <Pressable
              style={styles.mobileMenuBackdrop}
              onPress={() => setMobileMenuOpen(false)}
              accessibilityLabel={t('common.closeMenu')}
              accessibilityRole="button"
            />
            <View style={[styles.mobileSidebar, {paddingTop: insets.top + 24}]}>
              <TouchableOpacity
                style={[styles.mobileMenuCloseButton, {top: insets.top + 16}]}
                onPress={() => setMobileMenuOpen(false)}
                activeOpacity={0.7}
                accessibilityLabel={t('common.closeMenu')}
                accessibilityRole="button">
                <Text maxFontSizeMultiplier={1} style={styles.mobileMenuCloseIcon} accessible={false}>close</Text>
              </TouchableOpacity>
              {renderSidebarContent()}
            </View>
          </View>
        )}

        {/* Desktop Sidebar */}
        {isWide && (
          <View style={styles.sidebar}>
            {renderSidebarContent()}
          </View>
        )}

        {/* Main Content */}
        {children}
      </View>
    </DarkScreen>
  );
}

// ============================================================================
// Navigation Components
// ============================================================================

interface BackButtonProps {
  onPress: () => void;
  label?: string;
}

export function BackButton({onPress, label = 'Back'}: BackButtonProps) {
  return (
    <TouchableOpacity
      style={styles.backButton}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityLabel={label}
      accessibilityRole="button">
      <View style={styles.backButtonIcon}>
        <Text style={styles.backButtonIconText} accessible={false}>arrow_back</Text>
      </View>
      <Text style={styles.backButtonLabel}>{label}</Text>
    </TouchableOpacity>
  );
}

interface MenuItemProps {
  icon: string;
  label: string;
  onPress: () => void;
  description?: string;
  isDestructive?: boolean;
  isActive?: boolean;
}

export function MenuItem({icon, label, onPress, description, isDestructive, isActive}: MenuItemProps) {
  return (
    <TouchableOpacity
      style={[
        styles.menuItem,
        isDestructive && styles.menuItemDestructive,
        isActive && styles.menuItemActive,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityLabel={description ? `${label}. ${description}` : label}
      accessibilityRole="menuitem">
      <Text maxFontSizeMultiplier={1} style={[
        styles.menuItemIcon,
        styles.materialIcon,
        isDestructive && styles.menuItemIconDestructive,
        isActive && styles.menuItemIconActive,
      ]} accessible={false}>
        {icon}
      </Text>
      <View style={styles.menuItemText}>
        <Text style={[
          styles.menuItemLabel,
          isDestructive && styles.menuItemLabelDestructive,
          isActive && styles.menuItemLabelActive,
        ]}>
          {label}
        </Text>
        {description && (
          <Text style={styles.menuItemDescription}>{description}</Text>
        )}
      </View>
    </TouchableOpacity>
  );
}

// ============================================================================
// Typography Components
// ============================================================================

interface HeadingProps {
  children: React.ReactNode;
  style?: TextStyle;
}

export function Heading({children, style}: HeadingProps) {
  return <Text style={[styles.heading, style]}>{children}</Text>;
}

export function SubHeading({children, style}: HeadingProps) {
  return <Text style={[styles.subHeading, style]}>{children}</Text>;
}

export function BodyText({children, style}: HeadingProps) {
  return <Text style={[styles.bodyText, style]}>{children}</Text>;
}

export function MutedText({children, style}: HeadingProps) {
  return <Text style={[styles.mutedText, style]}>{children}</Text>;
}

// ============================================================================
// Card Components
// ============================================================================

interface ConnectServiceCardProps {
  icon: string;
  title: string;
  description: string;
  buttonLabel: string;
  onConnect: () => void;
  disabled?: boolean;
}

export function ConnectServiceCard({icon, title, description, buttonLabel, onConnect, disabled}: ConnectServiceCardProps) {
  return (
    <View style={styles.connectServiceContainer} accessibilityLabel={`${title}. ${description}`}>
      <View style={styles.connectServiceCard}>
        <View style={styles.connectServiceIconBg}>
          <Text maxFontSizeMultiplier={1} style={[styles.connectServiceIcon, styles.materialIcon]} accessible={false}>{icon}</Text>
        </View>
        <Heading style={styles.connectServiceTitle}>{title}</Heading>
        <BodyText style={styles.connectServiceDescription}>{description}</BodyText>
        <Button label={buttonLabel} icon="link" onPress={onConnect} disabled={disabled} />
      </View>
    </View>
  );
}

interface InfoCardProps {
  icon: string;
  title: string;
  description: string;
  status?: {
    label: string;
    isPositive?: boolean;
  };
  action?: {
    label: string;
    onPress: () => void;
  };
  onPress?: () => void;
}

export function InfoCard({icon, title, description, status, action, onPress}: InfoCardProps) {
  const accessibilityLabel = status
    ? `${title}. ${description}. ${status.label}`
    : `${title}. ${description}`;

  const content = (
    <>
      <Text maxFontSizeMultiplier={1} style={[styles.infoCardIcon, styles.materialIcon]} accessible={false}>{icon}</Text>
      <View style={styles.infoCardContent}>
        <Text style={styles.infoCardTitle}>{title}</Text>
        <Text style={styles.infoCardDescription}>{description}</Text>
        {status && (
          <View style={styles.infoCardStatus}>
            <View style={[
              styles.statusDot,
              status.isPositive ? styles.statusDotPositive : styles.statusDotNegative,
            ]} />
            <Text style={[
              styles.statusLabel,
              status.isPositive ? styles.statusLabelPositive : styles.statusLabelNegative,
            ]}>
              {status.label}
            </Text>
          </View>
        )}
        {action && (
          <TouchableOpacity onPress={action.onPress} activeOpacity={0.7} accessibilityLabel={action.label} accessibilityRole="button">
            <Text style={styles.infoCardAction}>{action.label}</Text>
          </TouchableOpacity>
        )}
      </View>
      {onPress && (
        <Text maxFontSizeMultiplier={1} style={[styles.infoCardChevron, styles.materialIcon]} accessible={false}>chevron_right</Text>
      )}
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity style={styles.infoCard} onPress={onPress} activeOpacity={0.7} accessibilityLabel={accessibilityLabel} accessibilityRole="button">
        {content}
      </TouchableOpacity>
    );
  }

  return <View style={styles.infoCard} accessibilityLabel={accessibilityLabel} accessibilityRole="summary">{content}</View>;
}

// ============================================================================
// Form Components
// ============================================================================

interface ButtonProps {
  label: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'destructive';
  disabled?: boolean;
  icon?: string;
}

export function Button({label, onPress, variant = 'primary', disabled, icon}: ButtonProps) {
  return (
    <TouchableOpacity
      style={[
        styles.button,
        variant === 'primary' && styles.buttonPrimary,
        variant === 'secondary' && styles.buttonSecondary,
        variant === 'destructive' && styles.buttonDestructive,
        disabled && styles.buttonDisabled,
      ]}
      onPress={onPress}
      activeOpacity={0.8}
      disabled={disabled}
      accessibilityLabel={label}
      accessibilityRole="button">
      {icon && (
        <Text maxFontSizeMultiplier={1} style={[styles.buttonIcon, styles.materialIcon]} accessible={false}>{icon}</Text>
      )}
      <Text style={[
        styles.buttonLabel,
        variant === 'secondary' && styles.buttonLabelSecondary,
        variant === 'destructive' && styles.buttonLabelDestructive,
      ]}>
        {label}
      </Text>
    </TouchableOpacity>
  );
}

// ============================================================================
// Styles
// ============================================================================

const styles = StyleSheet.create({
  // Screen
  screen: {
    flex: 1,
    backgroundColor: theme.background,
  },
  backgroundContainer: {
    ...StyleSheet.absoluteFillObject,
  },
  backgroundImage: {
    width: '100%',
    height: '100%',
    opacity: 0.4,
  },
  backgroundOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(28, 25, 23, 0.6)',
  },
  backgroundSolid: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: theme.background,
  },

  // Glass Panel
  glassPanel: {
    backgroundColor: theme.surfaceLight,
    backdropFilter: 'blur(24px)',
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.borderLight,
    padding: 24,
  } as any,
  glassPanelCentered: {
    alignItems: 'center',
  },

  // Back Button
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 24,
  },
  backButtonIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.surface,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  backButtonIconText: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: theme.text,
  },
  backButtonLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: theme.text,
  },

  // Material Icon
  materialIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
  },

  // Menu Item
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: 'transparent',
  },
  menuItemDestructive: {
    marginTop: 'auto',
  },
  menuItemIcon: {
    fontSize: 24,
    color: theme.textMuted,
  },
  menuItemIconDestructive: {
    color: theme.error,
  },
  menuItemIconActive: {
    color: theme.primary,
  },
  menuItemText: {
    flex: 1,
  },
  menuItemLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: theme.textMuted,
  },
  menuItemLabelDestructive: {
    color: theme.error,
  },
  menuItemLabelActive: {
    color: theme.primary,
  },
  menuItemActive: {
    backgroundColor: theme.primaryLight,
    borderRadius: 12,
  },
  menuItemDescription: {
    fontSize: 14,
    color: theme.textSubtle,
    marginTop: 2,
  },

  // Typography
  heading: {
    fontSize: 28,
    fontWeight: '700',
    color: theme.heading,
  },
  subHeading: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.text,
  },
  bodyText: {
    fontSize: 16,
    color: theme.text,
    lineHeight: 24,
  },
  mutedText: {
    fontSize: 14,
    color: theme.textMuted,
  },

  // Info Card
  infoCard: {
    flexDirection: 'row',
    backgroundColor: theme.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.borderLight,
    padding: 16,
    gap: 16,
  },
  infoCardIcon: {
    fontSize: 28,
    color: theme.primary,
    marginTop: 2,
  },
  infoCardContent: {
    flex: 1,
  },
  infoCardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.text,
  },
  infoCardDescription: {
    fontSize: 14,
    color: theme.textMuted,
    marginTop: 4,
  },
  infoCardStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusDotPositive: {
    backgroundColor: theme.success,
  },
  statusDotNegative: {
    backgroundColor: theme.error,
  },
  statusLabel: {
    fontSize: 12,
    fontWeight: '500',
  },
  statusLabelPositive: {
    color: theme.success,
  },
  statusLabelNegative: {
    color: theme.error,
  },
  infoCardAction: {
    fontSize: 12,
    fontWeight: '700',
    color: theme.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 8,
  },
  infoCardChevron: {
    fontSize: 24,
    color: theme.textMuted,
    alignSelf: 'center',
  },

  // Button
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    minHeight: 52,
  },
  buttonPrimary: {
    backgroundColor: theme.primary,
  },
  buttonSecondary: {
    backgroundColor: theme.surfaceLight,
    borderWidth: 1,
    borderColor: theme.border,
  },
  buttonDestructive: {
    backgroundColor: theme.errorBg,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  buttonIcon: {
    fontSize: 20,
    color: theme.background,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.background,
  },
  buttonLabelSecondary: {
    color: theme.text,
  },
  buttonLabelDestructive: {
    color: theme.error,
  },

  // Settings Layout
  settingsContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  sidebar: {
    width: 280,
    backgroundColor: theme.surface,
    backdropFilter: 'blur(24px)',
    borderRightWidth: 1,
    borderRightColor: theme.borderLight,
    padding: 24,
    justifyContent: 'space-between',
  } as any,
  sidebarContent: {
    flex: 1,
  },
  sidebarTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: theme.heading,
    marginBottom: 32,
    paddingHorizontal: 8,
  },
  menuList: {
    gap: 4,
  },
  sidebarMenuItemActive: {
    backgroundColor: theme.surfaceLight,
  },
  sidebarMenuItemIconActive: {
    color: theme.primary,
  },
  sidebarMenuItemLabelActive: {
    color: theme.text,
  },

  // Mobile Menu
  mobileMenuButton: {
    position: 'absolute',
    top: 16,
    left: 16,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.surface,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    borderWidth: 1,
    borderColor: theme.borderLight,
  },
  mobileMenuButtonIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.text,
  },
  mobileMenuOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 100,
    flexDirection: 'row',
  },
  mobileMenuBackdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  mobileSidebar: {
    width: 280,
    maxWidth: '85%',
    backgroundColor: theme.surface,
    height: '100%',
    padding: 24,
    paddingTop: 72,
  },
  mobileMenuCloseButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.surfaceLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mobileMenuCloseIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 24,
    color: theme.text,
  },

  // Connect Service Card
  connectServiceContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  connectServiceCard: {
    alignItems: 'center',
    backgroundColor: theme.surface,
    padding: 48,
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.borderLight,
    maxWidth: 480,
    width: '100%',
  },
  connectServiceIconBg: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: theme.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  connectServiceIcon: {
    fontSize: 40,
    color: theme.primary,
  },
  connectServiceTitle: {
    marginBottom: 12,
    textAlign: 'center',
  },
  connectServiceDescription: {
    color: theme.textMuted,
    textAlign: 'center',
    marginBottom: 32,
    maxWidth: 320,
  },
});

export {theme, styles as darkStyles};
